import { useController, useFormContext } from "react-hook-form";

import { Checkbox, CheckboxProps } from "./base";

export interface ControlledCheckboxProps extends Omit<
  CheckboxProps,
  "checked" | "onChange" | "onBlur" | "error" | "required" | "inputRef"
> {
  /** Field name registered on the surrounding react-hook-form context. */
  name: string;
  /** Read the error from a different field than `name`. */
  errorName?: string;
  rules?: {
    required?: { value: boolean; message: string };
    validate?: (val: boolean) => string | boolean | undefined;
  };
}

/**
 * react-hook-form–bound {@link Checkbox}: the form owns the checked state and
 * validation via `name`/`rules`. Reach for this inside a form; use the plain
 * controlled `Checkbox` when a non-form parent owns the value, or
 * `UncontrolledCheckbox` when the field is self-contained.
 */
export function ControlledCheckbox({
  name,
  errorName = "",
  rules = {},
  disabled = false,
  ...rest
}: ControlledCheckboxProps) {
  const { control, getFieldState } = useFormContext();
  const { field } = useController({ name, control, rules });
  const { error } = getFieldState(errorName ? errorName : name);

  return (
    <Checkbox
      {...rest}
      name={field.name}
      checked={!!field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      inputRef={field.ref}
      disabled={field.disabled || disabled}
      required={rules.required?.value === true}
      error={error?.message}
    />
  );
}

export default ControlledCheckbox;
